import React, { useState, useRef } from 'react';
import { Building2, FileText, Mail, Phone, AlertCircle, ImagePlus, User, X } from 'lucide-react';
import { TextInput } from '../../../shared/components/TextInput';
import { maskCNPJ, maskPhone } from '../../../shared/utils/validators';
import { uploadLogo } from '../api';
import { useCriarEmpresa } from '../hooks';
import {
  normalizeCompanyForm,
  validateCompanyForm,
  type CompanyFormValues,
} from '../validation/companyForms';

interface Props {
  onSuccess: () => void;
}

export const CompanyRegistrationForm: React.FC<Props> = ({ onSuccess }) => {
  const [formData, setFormData] = useState<CompanyFormValues>({
    corporateName: '',
    tradeName: '',
    cnpj: '',
    responsibleName: '',
    responsibleEmail: '',
    responsiblePhone: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const criarEmpresa = useCriarEmpresa();

  const validate = (): boolean => {
    const newErrors = validateCompanyForm(formData);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field: keyof CompanyFormValues, value: string) => {
    let processed = value;
    if (field === 'cnpj') processed = maskCNPJ(value);
    else if (field === 'responsiblePhone') processed = maskPhone(value);
    setFormData(prev => ({ ...prev, [field]: processed }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setLogoPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsLoading(true);
    try {
      const company = await criarEmpresa.mutateAsync(normalizeCompanyForm(formData));
      if (logoFile) await uploadLogo(company.id, logoFile);
      onSuccess();
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Erro ao cadastrar empresa.';
      setErrors(prev => ({ ...prev, submit: message }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <TextInput
        label="Razão Social *"
        icon={<FileText className="w-5 h-5" />}
        value={formData.corporateName}
        onChange={e => handleChange('corporateName', e.target.value)}
        error={errors.corporateName}
        autoComplete="organization"
      />

      <TextInput
        label="Nome Fantasia *"
        icon={<Building2 className="w-5 h-5" />}
        value={formData.tradeName}
        onChange={e => handleChange('tradeName', e.target.value)}
        error={errors.tradeName}
      />

      <TextInput
        label="CNPJ *"
        icon={<FileText className="w-5 h-5" />}
        value={formData.cnpj}
        onChange={e => handleChange('cnpj', e.target.value)}
        error={errors.cnpj}
        maxLength={18}
        inputMode="text"
      />

      <TextInput
        label="Nome do Responsável *"
        icon={<User className="w-5 h-5" />}
        value={formData.responsibleName}
        onChange={e => handleChange('responsibleName', e.target.value)}
        error={errors.responsibleName}
        autoComplete="name"
      />

      <div className="grid grid-cols-2 gap-4">
        <TextInput
          label="E-mail do Responsável *"
          type="email"
          icon={<Mail className="w-5 h-5" />}
          value={formData.responsibleEmail}
          onChange={e => handleChange('responsibleEmail', e.target.value)}
          error={errors.responsibleEmail}
          autoComplete="email"
        />
        <TextInput
          label="Telefone do Responsável *"
          icon={<Phone className="w-5 h-5" />}
          value={formData.responsiblePhone}
          onChange={e => handleChange('responsiblePhone', e.target.value)}
          error={errors.responsiblePhone}
          maxLength={16}
          inputMode="numeric"
        />
      </div>

      {/* Logo upload */}
      <div>
        <p className="text-sm text-gray-600 mb-2">Logo da empresa</p>
        {logoPreview ? (
          <div className="relative inline-block">
            <img
              src={logoPreview}
              alt="Preview da logo"
              className="w-24 h-24 rounded-lg object-cover border border-gray-200"
            />
            <button
              type="button"
              onClick={handleRemoveLogo}
              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-2 px-4 py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-blue-400 hover:text-blue-500 transition-colors"
          >
            <ImagePlus className="w-5 h-5" />
            Selecionar imagem
          </button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleLogoChange}
        />
      </div>

      {errors.submit && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {errors.submit}
          </p>
        </div>
      )}

      <button
        type="submit"
        disabled={isLoading}
        className={`w-full py-3 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        {isLoading ? 'Cadastrando...' : 'Cadastrar Empresa'}
      </button>
    </form>
  );
};
